import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { OverflowMenu, type OverflowItem } from '@/components/ui/menu';
import { useCompany } from '@/company/context';
import { useAppCatalogLocale } from '@/i18n/context';
import { useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/connection-access';
import nativeCopyCatalog from '@/i18n/ns/native-postgres';
import type { ConnectionReadItem } from '../connection-read-access/api';
import { ConnectionMark } from '../your-connections/connection-mark';
import { ConnectionAccessSheet, connectionWho, type ConnectionToolLabel } from './policy-sheet';
import './policy.css';
import { useConnectionContextKey } from './use-current-request';

/** The same access card for every provider. Provider actions retain their existing callers. */
export function ConnectionAccessCard(props: Parameters<typeof CurrentConnectionAccessCard>[0]) {
  const key = useConnectionContextKey(props.item);
  return <CurrentConnectionAccessCard key={key} {...props} />;
}

function CurrentConnectionAccessCard({
  item,
  tools,
  busy = false,
  onSaved,
  onStale,
  onTest,
  onReconnect,
  onRemove,
  problem,
  removeMessage,
  logoId,
  identity,
}: {
  item: ConnectionReadItem;
  /** The app's vendored mark, when the host knows which app this account belongs to. */
  logoId?: string;
  /** The connected account (an email, a store), shown in place of the kind of connection. */
  identity?: string | null;
  tools?: ConnectionToolLabel[];
  busy?: boolean;
  onSaved: (next: ConnectionReadItem) => void;
  onStale: () => void;
  onTest?: () => void;
  onReconnect?: () => void;
  onRemove?: (fetchImpl?: typeof fetch) => Promise<void>;
  problem?: string;
  removeMessage?: string;
}) {
  const copy = useNamespaceCopy(copyCatalog);
  const nativeCopy = useNamespaceCopy(nativeCopyCatalog);
  const locale = useAppCatalogLocale() ?? 'en';
  const companyName = useCompany().data?.company?.name ?? '';
  const [open, setOpen] = useState(false);
  const [initialAction, setInitialAction] = useState<'on' | 'off' | 'remove' | null>(null);
  const review = item.access.status === 'needs_review';
  const off = !item.access.enabled;
  const reconnect = ['needs_authorization', 'needs_reauthorization'].includes(item.status ?? '');
  const status = reconnect
    ? copy.statusReconnect
    : review
      ? copy.statusReconnect
      : off
        ? copy.statusOff
        : item.status === 'unavailable'
          ? copy.statusUnavailable
          : copy.statusConnected;
  // The account itself tells two connections apart; a provider's name is not the customer's concern.
  const kind =
    identity ||
    (item.provider === 'postgres'
      ? copy.kindDatabase
      : item.provider === 'custom-mcp'
        ? copy.kindCustom
        : null);
  const what = item.policy?.effectiveChanges === 'allowed' ? copy.whatReadWrite : copy.whatRead;
  const personal = item.policy?.ownChanges.allowed && item.policy.changes !== 'allowed';
  const openSheet = (action: 'on' | 'off' | 'remove' | null) => {
    setInitialAction(action);
    setOpen(true);
  };
  // The shared ⋯ menu, so outside click and Esc close it like every other menu.
  const menu: OverflowItem[] = [];
  if (onTest)
    menu.push({ key: 'test', label: copy.testConnection, disabled: busy, onSelect: onTest });
  if (onReconnect)
    menu.push({
      key: 'reconnect',
      label: nativeCopy.reconnect,
      disabled: busy,
      onSelect: onReconnect,
    });
  // Off: Turn on is the card's own button, so the menu does not repeat it.
  if (!off)
    menu.push({
      key: 'off',
      label: copy.turnOff,
      tone: 'danger',
      disabled: busy,
      separated: menu.length > 0,
      onSelect: () => openSheet('off'),
    });
  if (onRemove)
    menu.push({
      key: 'remove',
      label: copy.remove,
      tone: 'danger',
      disabled: busy,
      onSelect: () => openSheet('remove'),
    });
  return (
    <article className="ws-access-card" data-connection-id={item.connectionId}>
      <span className="ws-access-card-icon" aria-hidden="true">
        <ConnectionMark
          name={item.name}
          src={null}
          logoId={logoId ?? (item.provider === 'postgres' ? 'postgresql' : undefined)}
          glyph={item.provider === 'custom-mcp' ? 'source' : undefined}
        />
      </span>
      <div className="ws-access-card-name">
        <h3>{item.name}</h3>
        <span
          className="ws-access-status"
          data-state={review || reconnect ? 'review' : off ? 'off' : 'on'}
        >
          {status}
        </span>
      </div>
      {kind && <p className="ws-access-card-kind">{kind}</p>}
      <p className="ws-access-card-summary">
        {connectionWho(item, companyName, copy, locale)} · {what}
        {personal ? ` ${copy.youSuffix}` : ''}
      </p>
      {(problem || item.status === 'unavailable') && (
        <p className="ws-access-card-summary ws-access-note" role={problem ? 'status' : undefined}>
          {problem ?? `${copy.statusUnavailable}. ${copy.failureUnknown}`}
        </p>
      )}
      <div className="ws-access-card-actions">
        {/* A turned-off connection's way back is in view: Turn on opens the same current access review. */}
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => openSheet(item.canSet && off ? 'on' : null)}
        >
          {!item.canSet ? copy.cardViewAccess : off ? copy.turnOn : copy.cardEditAccess}
        </Button>
        {item.canSet && <OverflowMenu variant="secondary" items={menu} />}
      </div>
      {open && (
        <ConnectionAccessSheet
          item={item}
          initialAction={initialAction}
          companyName={companyName}
          tools={tools}
          onClose={() => setOpen(false)}
          onSaved={onSaved}
          onStale={onStale}
          onRemove={onRemove}
          removeMessage={removeMessage}
        />
      )}
    </article>
  );
}
